import { type Metadata } from 'next'

/* Helper to make label types from a list type */
export type LabelType<T extends string, K = unknown> = {
  [key in T]: {
    label: string
  } & K
}

/* Helper to make accurate string-based Records */
export type Dict<T> = { [key: string]: T | undefined }

/* Helper to make a promisified version of a type */
export type Promisify<T> = { [K in keyof T]: Promise<T[K]> }

/* Helper to make a partial version of a type except for one property */
export type PartialExcept<T, K extends keyof T> = Partial<Omit<T, K>> & Pick<T, K>

/* Omit that distributes over unions. */
export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never

/* An array with at least one item is guaranteed */
export type NonEmptyArray<T> = [T, ...T[]]

export const SITE_URL = process.env.BUILD_TYPE !== 'prod' ? 'https://beta.edhrec.com' : 'https://edhrec.com'

/* Site-level metadata defaults */
export const METADATA_DEFAULTS: Metadata = {
  description: 'EDHREC',
  icons: { apple: '/logo192.png' },
  metadataBase: new URL(SITE_URL),
  title: 'EDHREC',
  twitter: { card: 'summary' },
}

export type UtmType = {
  campaign?: string
  content?: string
  medium?: string
  source?: string
  term?: string
}

export const CONTENT_WARNING_URL = 'https://magic.wizards.com/en/news/announcements/depictions-racism-magic-2020-06-10'

export const RELATED_URLS = {
  bluesky: 'https://bsky.app/profile/edhrec.com',
  facebook: 'https://www.facebook.com/EDHREC1',
  instagram: 'https://www.instagram.com/EDHREC',
  patreon: 'https://www.patreon.com/edhrec',
  tiktok: 'https://www.tiktok.com/@edhrec',
} as const

export const RECAPTCHA_KEY = '6Ld7ckoqAAAAACdJC0eF5Ak7de764U3u6RS4BtYi'

export const ARTICLE_URLS = {
  BRACKETS: '/articles/wotc-introduces-new-bracket-system-for-edh',
  BRACKETS_GUIDE: '/guides/edhrec-guide-to-commander-brackets',
  COMBO_VOTE: '/articles/introducing-the-commander-bracket-combo-vote',
  FIERY: '/articles/fireicescoremath',
  LIFT: '/articles/changelog-replacing-synergy-with-lift-on-edhrecs-card-pages',
} as const

export const CARD_IMAGE_BASE = 'https://card-images.edhrec.com'

export const CARDART = {
  AD_NAUSEAM: `${CARD_IMAGE_BASE}/art/front/a/9/a9f2c53e-ff58-4aa8-89a6-4f45628cc571.webp`,
  ALL_FATES_SCROLL: `${CARD_IMAGE_BASE}/art/front/3/a/3a5ed010-cb17-45df-b169-ebc807dae534.webp`,
  ATRAXA_PRAETORS_VOICE_2XM: `${CARD_IMAGE_BASE}/art/front/d/0/d0d33d52-3d28-4635-b985-51e126289259.webp`,
  BIRDS_OF_PARADISE: `${CARD_IMAGE_BASE}/art/front/3/d/3d69a3e0-6a2e-475a-964e-0affed1c017d.webp`,
  BLOOD_MOON: `${CARD_IMAGE_BASE}/art/front/d/0/d072e9ca-aae7-45dc-8025-3ce590bae63f.webp`,
  BREAKTHROUGH: `${CARD_IMAGE_BASE}/art/front/a/5/a59a2cea-3b65-43da-bc6e-0e3c82f25b3c.webp`,
  CODIE: `${CARD_IMAGE_BASE}/art/front/e/a/ea476ee1-67d9-4dd8-a5ac-f68a155eb18b.webp`,
  CRIME_NOVELIST: `${CARD_IMAGE_BASE}/art/front/1/4/14a5cd7c-b0b1-4ffa-a806-bb0e73baffad.webp`,
  CYBERNETICA_DATASMITH: `${CARD_IMAGE_BASE}/art/front/2/8/28624370-ba28-4879-bd43-0a00761e780d.webp`,
  DELIVERY_MOOGLE: `${CARD_IMAGE_BASE}/art/front/f/5/f58840dc-c641-4092-8b67-9c0d449af715.webp`,
  DELVER_OF_SECRETS: `${CARD_IMAGE_BASE}/art/front/6/9/6904ea20-e504-47da-95a0-08739fdde260.webp`,
  DENRY_KLIN: `${CARD_IMAGE_BASE}/art/front/a/c/ac40ca08-50e6-46e6-9d50-c4e84325620d.webp`,
  DOOR_OF_DESTINIES: `${CARD_IMAGE_BASE}/art/front/7/5/75fd3fea-158a-4575-84a8-879ee9404242.webp`,
  EDGAR_MARKOV: `${CARD_IMAGE_BASE}/art/front/a/5/a577ba08-0aa8-45be-aa83-d5078770127c.webp`,
  ELVISH_MYSTIC: `${CARD_IMAGE_BASE}/art/front/e/b/eb654a76-62ee-4fd8-83d4-cb912211a9a0.webp`,
  GIADA: `${CARD_IMAGE_BASE}/art/front/8/a/8ae6fc26-cfad-4da8-98d9-49c27c24d293.webp`,
  GOBLIN_VANDAL: `${CARD_IMAGE_BASE}/art/front/b/7/b7ad3b81-f706-4b33-b1ec-7600182a5232.webp`,
  JALUM_TOME_6ED: `${CARD_IMAGE_BASE}/art/front/a/0/a0489c2d-bcf9-4179-b07a-781c6ac07980.webp`,
  KAALIA: `${CARD_IMAGE_BASE}/art/front/4/b/4b71d89b-7ba4-406f-8736-ac62b9864f21.webp`,
  LAND: `${CARD_IMAGE_BASE}/art/front/7/c/7c2839fa-33eb-47fb-9541-77c68612aa02.webp`,
  MAGNIFIYING_GLASS: `${CARD_IMAGE_BASE}/art/front/c/6/c606a71f-8fb0-486b-955f-727ebf436946.webp`,
  MANIFOLD_KEY: `${CARD_IMAGE_BASE}/art/front/0/f/0f5a15cd-a359-48c9-9280-ce139f1fb061.webp`,
  MEREN_OF_CLAN_NEL_TOTH: `${CARD_IMAGE_BASE}/art/front/5/0/508b1442-bf2c-4ad6-9bcf-bd894e081ab6.webp`,
  PARALLEL_LIVES: `${CARD_IMAGE_BASE}/art/front/0/1/01033dae-fec1-41f2-b7f2-cc6a43331790.webp`,
  PATIENT_REBUILDING: `${CARD_IMAGE_BASE}/art/front/2/2/2256e07f-a35a-4393-9744-045564d5770b.webp`,
  PEER_INTO_THE_ABYSS: `${CARD_IMAGE_BASE}/art/front/a/a/aac00055-640e-4749-8d23-d242e6d0b23a.webp`,
  PONDER: `${CARD_IMAGE_BASE}/art/front/d/c/dc69f960-68ba-4315-8146-6a7a82047503.webp`,
  SOL_RING: `${CARD_IMAGE_BASE}/art/front/2/1/219210d3-263c-41c4-9211-0e59b2d4ccf3.webp`,
  SPELLBOOK_PLST: `${CARD_IMAGE_BASE}/art/front/6/d/6d658d91-7387-48e6-889e-42b882598913.webp`,
  SQUELCH: `${CARD_IMAGE_BASE}/art/front/2/9/29421dd2-70a7-4623-afe0-ca4cb415ec87.webp`,
  STARFIELD_VOCALIST: `${CARD_IMAGE_BASE}/art/front/d/e/deca0b2a-e7f3-444a-883d-7c41dd62c9cc.webp`,
  SYGG_RIVER_GUIDE: `${CARD_IMAGE_BASE}/art/front/f/b/fb1aefaf-1b96-4c08-a73e-98e401655965.webp`,
  THASSAS_ORACLE: `${CARD_IMAGE_BASE}/art/front/7/2/726e8b29-13e9-4138-b6a9-d2a0d8188d1c.webp`,
  THE_UR_DRAGON: `${CARD_IMAGE_BASE}/art/front/1/0/10d42b35-844f-4a64-9981-c6118d45e826.webp`,
  TOTALLY_LOST: `${CARD_IMAGE_BASE}/art/front/c/3/c36f01a5-82bf-4fc6-9396-4410067c351b.webp`,
  TRANSCENDENT_MESSAGE: `${CARD_IMAGE_BASE}/art/front/3/2/3261c7e6-45be-44d1-8855-026359ba0ae7.webp`,
  UNCOVERED_CLUES: `${CARD_IMAGE_BASE}/art/front/9/d/9dd24556-994f-4480-835e-11d4443f0700.webp`,
  VANISH_INTO_MEMORY: `${CARD_IMAGE_BASE}/art/front/6/0/60dd9f6e-c848-4670-8de1-06f328ea7f71.webp`,
  WINDS_OF_CHANGE: `${CARD_IMAGE_BASE}/art/front/7/3/735b8aec-62d4-46db-9a68-a6c69cb6fd98.webp`,
} as const

export const SPLASHART = {
  EDDIE_WIDE: 'https://cloudflare.edhrec.com/articles/wp-content/uploads/2026/08/edhrec-snail-banner.webp',
} as const

/* https://www.gdpradvisor.co.uk/gdpr-countries */
export const GDPR_AFFECTED_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'GB',
  'HR',
  'HU',
  'IE',
  'IT',
  'LI',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
  'UK',
  /* Non-GDPR but similar laws */
  'CH',
  'BH',
  'IS',
  'QA',
  'TR',
  'KE',
  'MU',
  'NG',
  'ZA',
  'UG',
  'JP',
  'KR',
  'NZ',
  'AR',
  'BR',
  'UY',
  'CA',
]

export const ENDPOINT_BASES = {
  api: process.env.NEXT_PUBLIC_EDHREC_API_URL,
  apiCloudflare: process.env.NEXT_PUBLIC_EDHREC_API_URL_CLOUDFLARE,
  internal: '',
  json: process.env.NEXT_PUBLIC_EDHREC_JSON_URL,
  jsonCloudflare: process.env.NEXT_PUBLIC_EDHREC_JSON_URL_CLOUDFLARE,
  wordpress: 'https://edhrec.com/articles/wp-json',
}
export type EndpointBaseType = keyof typeof ENDPOINT_BASES

export const MAILER_URL = 'https://glgcw6nmtk4k2m2orq7dlrtllu0deart.lambda-url.us-east-1.on.aws'
export const RANDOM_URL = 'https://edhrec.com/random'

export type QuillOpType = { attributes?: Dict<unknown>; insert: Dict<string> | string }
